import React, { useEffect, useState } from "react";
import { Modal } from "antd";
import { axiosInstance } from "api/axios";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import "./MarketplaceStatus.css";

const reasons = ["Awaiting grid connection offer", "Awaiting funding", "Awaiting planning permission", "Project timeline changed", "Other"];
const monthLabel = value => value ? new Date(`${value}-01T12:00:00`).toLocaleDateString("en-GB", { month: "long", year: "numeric" }) : "";

function StatusIcon({ paused }) {
  return <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor" aria-hidden="true">{paused ? <><rect x="3" y="2" width="2" height="8" rx=".6" /><rect x="7" y="2" width="2" height="8" rx=".6" /></> : <circle cx="6" cy="6" r="3" />}</svg>;
}

function EffectIcon({ type }) {
  return <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {type === "visibility" ? <><path d="M3 3l18 18M10.6 10.6a2 2 0 002.8 2.8M9 5.5A11 11 0 0112 5c6 0 10 7 10 7a16 16 0 01-3 3.7M6 6.5A18 18 0 002 12s4 7 10 7a11 11 0 005-1.2" /></> : type === "bidders" ? <><circle cx="9" cy="8" r="3" /><path d="M3 21v-3a6 6 0 0112 0v3M16 5a3 3 0 010 6M18 14a5 5 0 013 4v3" /></> : <><rect x="5" y="10" width="14" height="11" rx="2" /><path d="M8 10V6a4 4 0 018 0v4" /></>}
  </svg>;
}

export function StatusBadge({ status }) {
  const paused = status === "paused";
  return <span className={`marketplace-badge ${paused ? "marketplace-badge--paused" : "marketplace-badge--active"}`}><StatusIcon paused={paused} />{paused ? "Paused" : "Active"}</span>;
}

export function PauseNotice({ site }) {
  if (site?.marketplaceStatus !== "paused") return null;
  return <div className="marketplace-pause-notice">
    <span className="marketplace-pause-notice__icon"><StatusIcon paused /></span>
    <div><p className="marketplace-pause-notice__title">Paused by the project owner{site.pauseReason ? ` · ${site.pauseReason}` : ""}</p>
      {site.pauseNote && <p className="marketplace-pause-notice__note">{site.pauseNote}</p>}
      <p className="marketplace-pause-notice__meta">{site.expectedResumeDate ? `Expected to resume: ${monthLabel(site.expectedResumeDate)} · ` : ""}Existing bids are kept. No new bids while paused.</p>
    </div>
  </div>;
}

export default function MarketplaceStatus({ site, editable = false }) {
  const dispatch = useDispatch();
  const [currentSite, setCurrentSite] = useState(site);
  const [note, setNote] = useState(site?.pauseNote || "");
  const [reason, setReason] = useState(site?.pauseReason || "");
  const [resumeDate, setResumeDate] = useState(site?.expectedResumeDate || "");
  const [notify, setNotify] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setCurrentSite(site); setEditing(false); }, [site]);
  const paused = currentSite?.marketplaceStatus === "paused";
  const close = () => { if (!saving) setEditing(false); };
  const open = () => {
    setNote(currentSite?.pauseNote || ""); setReason(currentSite?.pauseReason || "");
    setResumeDate(currentSite?.expectedResumeDate || ""); setNotify(false); setEditing(true);
  };
  const save = async next => {
    setSaving(true);
    try {
      const response = await axiosInstance.patch(`/sites/${site._id}/marketplace-status`, {
        marketplaceStatus: next, pauseNote: next === "paused" ? note : "",
        pauseReason: next === "paused" ? reason : "", expectedResumeDate: next === "paused" ? resumeDate : "",
        notifyInstallers: next === "paused" && notify,
      });
      setCurrentSite(response.site); setEditing(false);
      dispatch({ type: "marketplace/statusUpdated", payload: response.site });
      if (response.emailFailures) toast.warning("Project paused, but some notification emails could not be sent.");
    } catch (error) { toast.error(error?.data?.message || "Unable to update project status"); }
    finally { setSaving(false); }
  };
  if (!editable) return <><StatusBadge status={currentSite?.marketplaceStatus} /><PauseNotice site={currentSite} /></>;
  return <>
    <section className="marketplace-status-panel" aria-label="Marketplace status">
      <div className="marketplace-status-panel__row">
        <div><p className="marketplace-status-panel__label">Marketplace status</p><p className="marketplace-status-panel__description">{paused ? "Hidden from new installers. Existing bidders can still view it." : "Visible to eligible installers on Marketplace."}</p></div>
        <div className="marketplace-status-toggle" role="group" aria-label="Project availability">
          <button type="button" aria-label="Make project active" aria-pressed={!paused} disabled={saving || !site?._id} className={!paused ? "is-active" : ""} onClick={() => paused && save("active")}><StatusIcon />Active</button>
          <button type="button" aria-label="Pause project" aria-pressed={paused} disabled={saving || !site?._id} className={paused ? "is-paused" : ""} onClick={() => !paused && open()}><StatusIcon paused />Paused</button>
        </div>
      </div>
      <PauseNotice site={currentSite} />
    </section>
    <Modal open={editing} onCancel={close} footer={null} width={510} centered destroyOnClose closable={!saving} maskClosable={!saving} keyboard={!saving} className="marketplace-pause-modal" title={<div className="marketplace-pause-heading"><span className="marketplace-pause-heading__icon"><StatusIcon paused /></span><div><h2>Pause project on Marketplace</h2><p>{site?.nameOfSite || site?.siteName}</p></div></div>}>
      <div className="marketplace-pause-dialog">
        <ul className="marketplace-pause-effects"><li><EffectIcon type="visibility" />Hidden from installers who haven’t bid on it.</li><li><EffectIcon type="bidders" />Installers who have bid can still see it, marked Paused.</li><li><EffectIcon type="lock" />No new bids until you set it back to Active.</li></ul>
        <div className="marketplace-pause-fields">
          <label htmlFor="pause-reason">Reason</label>
          <select id="pause-reason" value={reason} onChange={event => setReason(event.target.value)} disabled={saving}><option value="">Select a reason (optional)</option>{reasons.map(item => <option key={item}>{item}</option>)}</select>
          <label htmlFor="pause-note">Note for installers who have bid <span>(optional)</span></label>
          <textarea id="pause-note" value={note} maxLength={2000} onChange={event => setNote(event.target.value)} rows={3} disabled={saving} placeholder="Let installers know why the project is on hold…" aria-describedby="pause-note-help" />
          <p id="pause-note-help" className="marketplace-pause-help">Shown on their portal next to the Paused status.</p>
          <label htmlFor="pause-resume">Expected resume date <span>(optional)</span></label>
          <input id="pause-resume" type="month" value={resumeDate} onChange={event => setResumeDate(event.target.value)} disabled={saving} />
          <label className="marketplace-pause-checkbox"><input type="checkbox" checked={notify} onChange={event => setNotify(event.target.checked)} disabled={saving} />Email installers who have bid</label>
        </div>
        <div className="marketplace-pause-actions"><button type="button" onClick={close} disabled={saving} className="marketplace-pause-cancel">Cancel</button><button type="button" onClick={() => save("paused")} disabled={saving} className="marketplace-pause-confirm">{saving ? "Pausing…" : "Pause project"}</button></div>
      </div>
    </Modal>
  </>;
}
